import React from 'react';
import { InstagramCommentCard } from './Chat/InstagramCommentCard.tsx';
import { parseInstagramComment } from '../utils/instagramComment.ts';
import type { InstagramCommentData, InstagramCommentScope } from '../services/instagram.ts';
import { OptimizedMessage, NPSMessageData } from '../services/api.ts';
import AudioWaveform from './AudioWaveform.tsx';
import VideoPlayer from './VideoPlayer.tsx';
import OptimizedImageContent from './OptimizedImageContent.tsx';
import { NPSMessage } from './NPSMessage.tsx';
import { convertWahaUrlToProxy } from '../utils/wahaProxy.ts';
import { getExpiredMediaContent, ExpiredMediaContent } from '../utils/mediaRetention.ts';
import { parseStoredWhatsAppLocation } from '../utils/lastMessagePreview.ts';
import { Check, CheckCheck, Clock, ImageOff, MicOff, VideoOff, FileX, MapPin } from 'lucide-react';
import { messageAuthorLabel } from '../utils/messageAttribution.ts';
import TemplateMessageContent from './Chat/TemplateMessageContent.tsx';
import DeliveryFailureHint from './Chat/DeliveryFailureHint.tsx';
import type { MessageDeliveryError } from '../types/templateMessage.ts';
import { workflowMessageAsset } from '../hooks/useWorkflowMessageMedia.ts';
import WorkflowMediaContent from './Chat/WorkflowMediaContent.tsx';

const mediaDetails = (content: any): {url?: string; caption?: string; filename?: string} => {
  if (typeof content === 'object' && content) return content;
  if (typeof content === 'string') {
    try { const parsed = JSON.parse(content); return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}; } catch { return {url: content}; }
  }
  return {};
};
const MediaCaption: React.FC<{content: any}> = ({content}) => {
  const caption = mediaDetails(content)?.caption;
  return typeof caption === 'string' && caption ? <p className="mt-2 whitespace-pre-wrap break-words text-sm">{caption}</p> : null;
};

// Helper para renderizar ícone de status
const StatusIcon: React.FC<{ status?: string; isOwn: boolean; error?: MessageDeliveryError | null }> = ({ status, isOwn, error }) => {
  const baseClass = isOwn ? "text-white/75" : "text-gray-400";
  const readClass = isOwn ? "text-white/90" : "text-blue-400";

  if (!status) return null;

  switch (status) {
    case 'sending':
      return <Clock className={`w-3 h-3 ${baseClass}`} />;
    case 'sent':
      return <Check className={`w-3 h-3 ${baseClass}`} />;
    case 'delivered':
      return <CheckCheck className={`w-3 h-3 ${baseClass}`} />;
    case 'read':
      return <CheckCheck className={`w-3 h-3 ${readClass}`} />;
    case 'failed':
      return <DeliveryFailureHint error={error} isOwn={isOwn} />;
    default:
      return null;
  }
};

const getReplySenderName = (senderName?: string | null) => {
  const cleaned = (senderName || '').trim();
  if (!cleaned || cleaned.toLowerCase() === 'unknown' || cleaned.toLowerCase() === 'desconhecido') {
    return 'Mensagem';
  }
  return cleaned;
};

const ReplyPreview: React.FC<{ message: OptimizedMessage; isOwn: boolean }> = ({ message, isOwn }) => {
  if (!message.replyTo) return null;

  const preview = message.replyTo.content || '';
  const senderName = getReplySenderName(message.replyTo.senderName);

  return (
    <div className={`mb-2 rounded-xl border-l-4 px-2 py-1.5 text-xs ${
      isOwn
        ? 'border-white/60 bg-white/15 text-white/90'
        : 'border-sminy-primary bg-white text-gray-600'
    }`}>
      <div className={`font-semibold ${isOwn ? 'text-white' : 'text-sminy-primary'}`}>
        {senderName}
      </div>
      <div className="line-clamp-2 break-words">
        {preview || `[${message.replyTo.type || 'mensagem'}]`}
      </div>
    </div>
  );
};

const ReactionBar: React.FC<{ message: OptimizedMessage; isOwn: boolean }> = ({ message, isOwn }) => {
  const reactions = message.reactions || [];
  if (!reactions.length) return null;

  return (
    <div className={`mt-1 flex flex-wrap gap-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
      {reactions.map((reaction) => (
        <span
          key={`${reaction.actorId}_${reaction.emoji}`}
          className={`inline-flex h-6 min-w-6 items-center justify-center rounded-full border px-1.5 text-xs shadow-sm ${
            isOwn
              ? 'border-white/25 bg-white/20 text-white'
              : 'border-gray-200 bg-white text-gray-700'
          }`}
          title={reaction.actorName || undefined}
        >
          {reaction.emoji}
        </span>
      ))}
    </div>
  );
};

// Helper Component Wrapper para Mensagens Oficiais (Premium Look)
const OfficialMessageWrapper: React.FC<{
  children: React.ReactNode;
  isOwn: boolean;
  isOfficial: boolean;
  timestamp: string;
  status?: 'sending' | 'sent' | 'delivered' | 'read' | 'failed';
  message?: OptimizedMessage;
}> = ({ children, isOwn, isOfficial, timestamp, status, message }) => {
  const author = message ? messageAuthorLabel(message) : null;
  if (!isOfficial) {
    return (
      <div className="flex flex-col" data-delivery-popup-anchor>
        {message && <ReplyPreview message={message} isOwn={isOwn} />}
        {children}

        <div className="flex items-end justify-between gap-2 mt-1">
          {author && <span className={`max-w-[70%] truncate text-[10px] leading-tight ${isOwn ? 'text-white/80' : 'text-slate-500'}`} title={author}>{author}</span>}
          <span className="ml-auto flex items-center gap-1">
          <span className={`text-[10px] leading-none ${isOwn ? 'text-white/75' : 'text-gray-400'} flex items-center gap-1`}>
            {timestamp}
          </span>
          {isOwn && <StatusIcon status={status} isOwn={isOwn} error={message?.deliveryError} />}
          </span>
        </div>
        {message && <ReactionBar message={message} isOwn={isOwn} />}
      </div>
    );
  }

  // Estilos Premium para Mensagens Oficiais (ORIGINAL)
  const containerClasses = isOwn
    ? "relative"
    : "relative bg-gray-100 -m-3 p-3 rounded-2xl border border-gray-200 shadow-sm z-10";

  const headerColor = isOwn ? "text-white/90" : "text-emerald-700";
  const iconColor = isOwn ? "text-white" : "text-emerald-600";
  const borderColor = isOwn ? "border-white/20" : "border-gray-200";

  return (
    <div className={containerClasses} data-delivery-popup-anchor>
      {/* Premium Header */}
      <div className={`flex items-center gap-1.5 mb-2 pb-1.5 border-b ${borderColor}`}>
        <svg viewBox="0 0 24 24" className={`w-3.5 h-3.5 ${iconColor}`} fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
        </svg>
        <span className={`text-[10px] font-bold tracking-wider uppercase ${headerColor}`}>
          WhatsApp Oficial
        </span>
      </div>

      {message && <ReplyPreview message={message} isOwn={isOwn} />}
      {children}

      <div className="flex items-end justify-between gap-2 mt-1">
        {author && <span className={`max-w-[70%] truncate text-[10px] leading-tight ${isOwn ? 'text-white/80' : 'text-slate-500'}`} title={author}>{author}</span>}
        <span className="ml-auto flex items-center gap-1">
        <span className={`text-[10px] leading-none ${isOwn ? 'text-white/90' : 'text-emerald-700/80'} flex items-center gap-1`}>
          {timestamp}
        </span>
        {isOwn && <StatusIcon status={status} isOwn={isOwn} error={message?.deliveryError} />}
        </span>
      </div>
      {message && <ReactionBar message={message} isOwn={isOwn} />}
    </div>
  );
};

const ExpiredMediaNotice: React.FC<{
  expiredContent: ExpiredMediaContent;
  message: OptimizedMessage;
  isOwn: boolean;
}> = ({ expiredContent, message, isOwn }) => {
  const isOfficial = message.source_channel === 'official_whatsapp';
  const mediaType = expiredContent.expiredMediaType || message.type;
  const Icon = mediaType === 'image'
    ? ImageOff
    : mediaType === 'audio'
      ? MicOff
      : mediaType === 'video'
        ? VideoOff
        : FileX;

  const wrapperClasses = isOwn && isOfficial
    ? "rounded-2xl p-3 bg-sminy-primary text-white max-w-[300px]"
    : `${isOwn ? 'bg-sminy-primary text-white' : 'bg-gray-100 text-gray-700'} rounded-2xl p-3 max-w-[300px]`;

  return (
    <div className={wrapperClasses}>
      <OfficialMessageWrapper
        isOwn={isOwn}
        isOfficial={isOfficial}
        timestamp={formatChatTime(message.timestamp)}
        status={message.status}
        message={message}
      >
        <div className="flex items-center gap-2 text-sm leading-snug">
          <Icon className={`w-4 h-4 shrink-0 ${isOwn ? 'text-white/90' : 'text-gray-500'}`} />
          <span>{expiredContent.fallbackText}</span>
        </div>
      </OfficialMessageWrapper>
    </div>
  );
};

const formatChatTime = (timestamp: string) => {
  if (!timestamp) return '';
  const parts = timestamp.split(':');
  if (parts.length >= 2) {
    return `${parts[0]}:${parts[1]}`;
  }
  return timestamp;
};


/**
 * Função auxiliar para normalizar caminhos de mídia.
 * Se path for 'data:' ou 'http', marcamos como isBase64OrHttp = true e retornamos inalterado.
 * Caso seja 'client_6/clinic_4/video/...', preservamos o caminho completo.
 * Senão, retorna o path como está.
 */
function normalizeMediaPath(path: string): { isBase64OrHttp: boolean; finalPath: string } {
  // Se for base64 ou URL externa, retornamos sem mexer
  if (
    path.startsWith('data:') ||
    path.startsWith('http://') ||
    path.startsWith('https://') ||
    path.startsWith('/api/waha/media/') ||
    path.startsWith('/api/arquivos/media/')
  ) {
    return { isBase64OrHttp: true, finalPath: path };
  }

  // Tentar casar "client_X/clinic_Y/(image|video|audio)/arquivo"
  const regex = /^client_(\d+)\/clinic_(\d+)\/(image|video|audio)\/(.+)$/;
  const match = path.match(regex);
  if (match) {
    return {
      isBase64OrHttp: false,
      finalPath: path,
    };
  }

  // Se não bater no regex, devolve path inalterado
  return { isBase64OrHttp: false, finalPath: path };
}

interface OptimizedMessageContentProps {
  message: OptimizedMessage;
  isOwn: boolean;
  instagramScope?: InstagramCommentScope;
  canReplyComment?: boolean;
  onReplyComment?: (comment: InstagramCommentData) => void;
}

export const OptimizedMessageContent: React.FC<OptimizedMessageContentProps> = ({
  message,
  isOwn,
  instagramScope, canReplyComment, onReplyComment,
}) => {
  if (message.source_channel === 'instagram' && message.type === 'comment') {
    const comment = parseInstagramComment(message.content);
    return comment ? <InstagramCommentCard comment={comment} timestamp={formatChatTime(message.timestamp)}
      scope={instagramScope} canReply={canReplyComment} onReply={onReplyComment} />
      : <p className="rounded-xl border border-slate-200 p-3 text-xs text-slate-500">Comentário indisponível</p>;
  }
  // Classes base para estilizar as mensagens
  const baseClasses = `rounded-2xl p-3 break-words whitespace-pre-wrap max-w-[300px] ${isOwn ? 'bg-sminy-primary text-white' : 'bg-gray-100 text-gray-900'
    }`;

  // Formata a hora (HH:mm) a partir de string, se quiser algo simples
  const formatTime = formatChatTime;

  const expiredContent = getExpiredMediaContent(message.content, message.type);
  if (expiredContent && ['image', 'audio', 'video', 'file'].includes(message.type)) {
    return (
      <ExpiredMediaNotice
        expiredContent={expiredContent}
        message={message}
        isOwn={isOwn}
      />
    );
  }

  const workflowAsset = message.source_channel === 'official_whatsapp' ? workflowMessageAsset(message.content) : null;
  if (workflowAsset && (message.type === 'image' || message.type === 'audio' || message.type === 'video')) {
    return <div className={`max-w-[300px] ${isOwn ? 'rounded-2xl bg-sminy-primary p-3 text-white' : 'space-y-1'}`}>
      <OfficialMessageWrapper isOwn={isOwn} isOfficial timestamp={formatTime(message.timestamp)} status={message.status} message={message}>
        <WorkflowMediaContent assetId={workflowAsset} kind={message.type} messageId={message.databaseMessageId} isOwn={isOwn} />
        <MediaCaption content={message.content} />
      </OfficialMessageWrapper>
    </div>;
  }

  // ===================================
  // Caso seja mensagem de TEXTO
  // ===================================
  if (message.type === 'text') {
    const content = typeof message.content === 'string' ? message.content : '';
    return (
      <div className="group relative">
        <div className={baseClasses}>
          <OfficialMessageWrapper
            isOwn={isOwn}
            isOfficial={message.source_channel === 'official_whatsapp'}
            timestamp={formatTime(message.timestamp)}
            status={message.status}
            message={message}
          >
            <p className="text-sm whitespace-pre-wrap break-words">{content}</p>



          </OfficialMessageWrapper>
        </div>

      </div>
    );
  }

  // ===================================
  // Caso seja mensagem de TEMPLATE (Campanhas)
  // ===================================
  if (message.type === 'template') {
    return (
      <div className="group relative">
        <div className={baseClasses}>
          <OfficialMessageWrapper
            isOwn={isOwn}
            isOfficial={true} // Templates sempre são enviados via API Oficial
            timestamp={formatTime(message.timestamp)}
            status={message.status}
            message={message}
          >
            <TemplateMessageContent message={message} snapshot={message.templateSnapshot} isOwn={isOwn} />
          </OfficialMessageWrapper>
        </div>
      </div>
    );
  }

  if (message.type === 'location') {
    const location = parseStoredWhatsAppLocation(message.content);
    const mapUrl = location
      ? `https://www.google.com/maps/search/?api=1&query=${location.latitude},${location.longitude}`
      : null;

    return (
      <div className="group relative">
        <div className={baseClasses}>
          <OfficialMessageWrapper
            isOwn={isOwn}
            isOfficial={message.source_channel === 'official_whatsapp'}
            timestamp={formatTime(message.timestamp)}
            status={message.status}
            message={message}
          >
            <div className="flex items-start gap-2">
              <MapPin className={`mt-0.5 h-4 w-4 flex-shrink-0 ${isOwn ? 'text-white/85' : 'text-sminy-primary'}`} />
              <div className="min-w-0">
                <p className="text-sm font-medium">
                  {location?.name || 'Localização compartilhada'}
                </p>
                {location?.address && (
                  <p className={`mt-1 text-xs ${isOwn ? 'text-white/80' : 'text-gray-600'}`}>
                    {location.address}
                  </p>
                )}
                {mapUrl && (
                  <a
                    href={mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`mt-2 inline-block text-xs font-medium underline ${isOwn ? 'text-white/90' : 'text-sminy-primary'}`}
                  >
                    Abrir localização no mapa
                  </a>
                )}
              </div>
            </div>
          </OfficialMessageWrapper>
        </div>
      </div>
    );
  }

  // ===================================
  // Caso seja mensagem de IMAGEM
  // ===================================
  if (message.type === 'image') {
    const imageContent = message.content;
    let imageUrl = '';
    let mediaPath: string | undefined;
    let needsLoading = false;

    if (typeof imageContent === 'string') {
      // Se for base64 (data:) ou blob, usamos diretamente em imageUrl
      if (imageContent.startsWith('data:image/') || imageContent.startsWith('blob:')) {
        imageUrl = convertWahaUrlToProxy(imageContent);
      } else if (imageContent.startsWith('http')) {
        // ✅ FIX: URLs públicas (app.sminy.ai, dev.sminy.ai) não precisam de proxy
        if (imageContent.includes('sminy.ai/arquivos/')) {
          imageUrl = imageContent;
        } else {
          // Verificar se é URL do WAHA que precisa de proxy
          const proxyUrl = convertWahaUrlToProxy(imageContent);
          imageUrl = proxyUrl;
        }
      } else if (imageContent.startsWith('/api/waha/media/')) {
        // 🔥 FIX: Se já for URL de proxy WAHA, usar diretamente
        imageUrl = imageContent;
      } else {
        // Normalizar caminho local
        const { isBase64OrHttp, finalPath } = normalizeMediaPath(imageContent);
        if (isBase64OrHttp) {
          // Se detectou data: ou http
          imageUrl = finalPath;
        } else {
          // Caminho local => usaremos mediaPath + needsLoading
          mediaPath = finalPath;
          needsLoading = true;
        }
      }
    } else if (imageContent && typeof imageContent === 'object') {
      // Se vier no formato { url, mediaPath, needsLoading }
      const contentObj = imageContent as {
        url?: string;
        mediaPath?: string;
        needsLoading?: boolean;
      };

      // 🔥 FIX: Para URLs de proxy WAHA em objetos, usar diretamente
      if (contentObj.url && contentObj.url.startsWith('/api/waha/media/')) {
        imageUrl = convertWahaUrlToProxy(contentObj.url);
      } else if (contentObj.url && contentObj.url.startsWith('http')) {
        // ✅ FIX: URLs públicas (app.sminy.ai, dev.sminy.ai) não precisam de proxy
        if (contentObj.url.includes('sminy.ai/arquivos/')) {
          imageUrl = contentObj.url;
        } else {
          // Aplicar proxy WAHA se necessário
          const proxyUrl = convertWahaUrlToProxy(contentObj.url);
          imageUrl = proxyUrl;
        }
      } else {
        imageUrl = contentObj.url || '';
      }
      mediaPath = contentObj.mediaPath;
      needsLoading = !!contentObj.needsLoading;
    }

    // Se ao final não há nem imageUrl nem mediaPath, erro
    if (!imageUrl && !mediaPath) {
      console.error('URL da imagem não encontrada');
      return (
        <div className="max-w-[300px] bg-red-50 border border-red-200 p-3 rounded-lg text-red-500 text-sm">
          Erro ao carregar imagem
        </div>
      );
    }

    const isOfficial = message.source_channel === 'official_whatsapp';

    // 🔥 FIX: Para mensagens oficiais isOwn, adicionar wrapper azul igual ao texto
    const imageWrapperClasses = isOwn && isOfficial
      ? "rounded-2xl p-3 bg-sminy-primary text-white max-w-[300px]"
      : "max-w-[300px] space-y-1";

    return (
      <div className={imageWrapperClasses}>
        <OfficialMessageWrapper
          isOwn={isOwn}
          isOfficial={isOfficial}
          timestamp={formatTime(message.timestamp)}
          status={message.status}
          message={message}
        >
          <OptimizedImageContent
            src={imageUrl}
            mediaPath={mediaPath}
            needsLoading={needsLoading}
            alt="Imagem compartilhada"
            isOwn={isOwn}
          />
          <MediaCaption content={message.content} />
        </OfficialMessageWrapper>
      </div>
    );
  }

  // ===================================
  // Caso seja mensagem de ÁUDIO
  // ===================================
  if (message.type === 'audio') {
    const audioContent = message.content;
    let audioUrl = '';
    let mediaPath: string | undefined;
    let needsLoading = false;

    if (typeof audioContent === 'string') {
      if (audioContent.startsWith('data:audio/') || audioContent.startsWith('blob:')) {
        audioUrl = convertWahaUrlToProxy(audioContent);
      } else if (audioContent.startsWith('http')) {
        // Verificar se é URL do WAHA que precisa de proxy
        const proxyUrl = convertWahaUrlToProxy(audioContent);
        audioUrl = proxyUrl;
      } else if (audioContent.startsWith('/api/waha/media/')) {
        // 🔥 FIX: Se já for URL de proxy WAHA, usar diretamente
        audioUrl = audioContent;
      } else {
        const { isBase64OrHttp, finalPath } = normalizeMediaPath(audioContent);
        if (isBase64OrHttp) {
          audioUrl = finalPath;
        } else {
          mediaPath = finalPath;
          needsLoading = true;
        }
      }
    } else if (audioContent && typeof audioContent === 'object') {
      const contentObj = audioContent as {
        url?: string;
        mediaPath?: string;
        needsLoading?: boolean;
      };

      // 🔥 FIX: Para URLs de proxy WAHA em objetos, usar diretamente
      if (contentObj.url && contentObj.url.startsWith('/api/waha/media/')) {
        audioUrl = convertWahaUrlToProxy(contentObj.url);
      } else if (contentObj.url && contentObj.url.startsWith('http')) {
        // Aplicar proxy WAHA se necessário
        const proxyUrl = convertWahaUrlToProxy(contentObj.url);
        audioUrl = proxyUrl;
      } else {
        audioUrl = contentObj.url || '';
      }
      mediaPath = contentObj.mediaPath;
      needsLoading = !!contentObj.needsLoading;

      // Se URL é um placeholder, ignorar e forçar carregamento via API
      if (audioUrl && audioUrl.includes('audio-placeholder')) {
        audioUrl = ''; // Limpa para forçar carregamento via mediaPath
        needsLoading = true;
      } else if (audioUrl && audioUrl.startsWith('blob:')) {
        needsLoading = false;
      }
    }

    // Extrair a duração, se disponível (MOVIDO PARA ANTES DO USO)
    let audioDuration: number | undefined;
    if (typeof audioContent === 'object' && audioContent) {
      audioDuration = (audioContent as any).duration;
    }

    return (
      <div className={`${isOwn ? 'bg-sminy-primary' : 'bg-gray-100'} rounded-2xl p-2 max-w-[300px]`}>
        <OfficialMessageWrapper
          isOwn={isOwn}
          isOfficial={message.source_channel === 'official_whatsapp'}
          timestamp={formatTime(message.timestamp)}
          status={message.status}
          message={message}
        >
          <AudioWaveform
            src={audioUrl}
            mediaPath={mediaPath}
            needsLoading={needsLoading}
            messageColor={isOwn ? 'light' : 'dark'}
            className="w-full"
            duration={audioDuration}
            fromMe={message.fromMe}
          />
        </OfficialMessageWrapper>
      </div>
    );
  }

  // ===================================
  // Caso seja mensagem de VÍDEO
  // ===================================
  if (message.type === 'video') {
    const videoContent = message.content;
    let videoUrl = '';
    let videoPath: string | undefined;
    let thumbnailUrl = '';
    let needsLoading = false;

    if (typeof videoContent === 'string') {
      // Se for data:video/ ou blob, definimos videoUrl
      if (videoContent.startsWith('data:video/') || videoContent.startsWith('blob:')) {
        videoUrl = convertWahaUrlToProxy(videoContent);
      } else if (videoContent.startsWith('http')) {
        // ✅ FIX: URLs públicas (app.sminy.ai, dev.sminy.ai) não precisam de proxy
        if (videoContent.includes('sminy.ai/arquivos/')) {
          videoUrl = videoContent;
          needsLoading = false;
        } else {
          // Verificar se é URL do WAHA que precisa de proxy
          const proxyUrl = convertWahaUrlToProxy(videoContent);

          if (proxyUrl !== videoContent) {
            // Se for URL WAHA convertida, usar o proxy diretamente como videoUrl
            videoUrl = proxyUrl;
            needsLoading = false;
          } else {
            // Para outras URLs HTTP, usar getMessageMedia
            videoPath = videoContent; // Usar como mediaPath para getMessageMedia
            needsLoading = true;
            videoUrl = '';
          }
        }
      } else if (videoContent.startsWith('/api/waha/media/')) {
        // 🔥 FIX: Se já for URL de proxy WAHA, usar diretamente
        videoUrl = videoContent;
        needsLoading = false;
      } else {
        // Caso contrário, interpretamos como caminho local
        const { isBase64OrHttp, finalPath } = normalizeMediaPath(videoContent);
        if (isBase64OrHttp) {
          videoUrl = finalPath;
        } else {
          videoPath = finalPath;
          needsLoading = true;
        }
      }
    } else if (videoContent && typeof videoContent === 'object') {
      const contentObj = videoContent as {
        url?: string;
        mediaPath?: string;
        thumbnailUrl?: string;
        mimeType?: string;
        needsLoading?: boolean;
        error?: string;
        fallbackText?: string;
      };

      // Verificar se há erro de conteúdo vazio
      if (contentObj.error === 'Conteúdo de mídia vazio') {
        console.error('Vídeo com conteúdo vazio');
        return (
          <div className="max-w-[300px] bg-red-50 border border-red-200 p-3 rounded-lg text-red-500 text-sm">
            <div className="flex items-center gap-2">
              <span>🎥</span>
              <span>{contentObj.fallbackText || 'Vídeo indisponível'}</span>
            </div>
            <div className="flex justify-end mt-2">
              <span className={`text-xs ${isOwn ? 'text-gray-300' : 'text-gray-500'}`}>
                {formatTime(message.timestamp)}
              </span>
            </div>
          </div>
        );
      }

      // 🔥 FIX: Para URLs de proxy WAHA (começam com /api/waha/media/), usar diretamente
      if (contentObj.url && contentObj.url.startsWith('/api/waha/media/')) {
        videoUrl = convertWahaUrlToProxy(contentObj.url);
        needsLoading = false;
      } else if (contentObj.url && contentObj.url.startsWith('http')) {
        // ✅ FIX: URLs públicas (app.sminy.ai, dev.sminy.ai) não precisam de proxy
        if (contentObj.url.includes('sminy.ai/arquivos/')) {
          videoUrl = contentObj.url;
          needsLoading = false;
        } else {
          // Verificar se é URL do WAHA que precisa de proxy
          const proxyUrl = convertWahaUrlToProxy(contentObj.url);

          if (proxyUrl !== contentObj.url) {
            // Se for URL WAHA convertida, usar o proxy
            videoPath = proxyUrl;
            needsLoading = true;
            videoUrl = '';
          } else {
            // Para outras URLs HTTP, usar getMessageMedia
            videoPath = contentObj.url;
            needsLoading = true;
            videoUrl = '';
          }
        }
      } else if (contentObj.url) {
        // URLs relativas ou outros formatos
        videoUrl = contentObj.url;
      }

      // Aplicar proxy WAHA se necessário no thumbnail
      if (contentObj.thumbnailUrl) {
        contentObj.thumbnailUrl = convertWahaUrlToProxy(contentObj.thumbnailUrl);
      }

      thumbnailUrl = contentObj.thumbnailUrl || '';

      // Se já definimos needsLoading=true acima, não sobrescrever
      if (!needsLoading) {
        needsLoading = !!contentObj.needsLoading;
      }
    }

    if (!videoUrl && !videoPath) {
      console.error('URL ou caminho do vídeo não encontrado');
      return (
        <div className="max-w-[300px] bg-red-50 border border-red-200 p-3 rounded-lg text-red-500 text-sm">
          Erro ao carregar vídeo
        </div>
      );
    }

    const isOfficial = message.source_channel === 'official_whatsapp';

    // 🔥 FIX: Para mensagens oficiais isOwn, adicionar wrapper azul igual ao texto
    const videoWrapperClasses = isOwn && isOfficial
      ? "rounded-2xl p-3 bg-sminy-primary text-white max-w-[300px]"
      : "max-w-[300px] space-y-1";

    // Renderiza o player
    return (
      <div className={videoWrapperClasses}>
        <OfficialMessageWrapper
          isOwn={isOwn}
          isOfficial={isOfficial}
          timestamp={formatTime(message.timestamp)}
          status={message.status}
          message={message}
        >
          <VideoPlayer
            src={videoUrl}
            mediaPath={videoPath}
            needsLoading={needsLoading}
            messageColor={isOwn ? 'light' : 'dark'}
            thumbnailUrl={thumbnailUrl}
            status={message.status}
            fromMe={message.fromMe}
            className="rounded-2xl overflow-hidden w-full"
          />
          <MediaCaption content={message.content} />
        </OfficialMessageWrapper>
      </div>
    );
  }

  // ===================================
  // Caso seja mensagem de NPS
  // ===================================
  if (message.type === 'nps') {
    const npsContent = message.content;
    let npsData: NPSMessageData;

    // Parse do conteúdo (pode ser string JSON ou objeto)
    try {
      let parsedContent: any;
      if (typeof npsContent === 'string') {
        parsedContent = JSON.parse(npsContent);
      } else if (typeof npsContent === 'object') {
        parsedContent = npsContent;
      }

      if (parsedContent?.nps_data) {
        npsData = parsedContent.nps_data;
      } else {
        throw new Error('No nps_data found');
      }
    } catch (e) {
      // Fallback caso não tenha nps_data estruturado
      npsData = {
        question: 'Em uma escala de 1 a 5, como você avalia nosso atendimento?',
        status: 'sent'
      };
    }

    return (
      <NPSMessage
        npsData={npsData}
        isOwn={isOwn}
        timestamp={message.timestamp}
      />
    );
  }

  if ((message.type === 'file' || message.type === 'document')) {
    const details = mediaDetails(message.content);
    const url = typeof details?.url === 'string' && /^(https?:\/\/|\/(?!\/))/.test(details.url) ? details.url : null;
    return <div className={baseClasses}>
      <OfficialMessageWrapper isOwn={isOwn} isOfficial={message.source_channel === 'official_whatsapp'} timestamp={formatTime(message.timestamp)} status={message.status} message={message}>
        {url ? <a href={url} target="_blank" rel="noopener noreferrer" className="break-words text-sm underline">{typeof details.filename === 'string' && details.filename ? details.filename : 'Abrir documento'}</a> : <p className="text-sm">Documento indisponível</p>}
        <MediaCaption content={message.content} />
      </OfficialMessageWrapper>
    </div>;
  }

  // Fallback caso o tipo não seja reconhecido
  return null;
};
